/**
 * PortalContext · centralised portal-type persistence.
 *
 * Stores the user's "portal" (customer / student / organizer / merchant /
 * hostel_owner / food_vendor / iprint_shop / admin) in localStorage so the
 * application can remember which login screen to show them across:
 *   • browser refresh
 *   • Add-to-Home-Screen launches
 *   • PWA cold starts
 *   • logout → login cycles
 *
 * The portal is the SINGLE source of truth for routing decisions outside the
 * authenticated app shell.  AuthContext handles tokens; PortalContext handles
 * "where do I send this user when they're NOT authenticated".
 */
import React, { createContext, useContext, useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "icard_portal";

/**
 * Portal registry — single source of truth.  Add a new portal by appending
 * one row here; all downstream routing picks it up automatically.
 *
 *   slug         URL-friendly portal identifier (used in /:slug, /:slug/login)
 *   loginPath    where the entry route forwards unauthenticated users
 *   label        display name used in the portal-selector grid
 *   icon         lucide-react icon name
 *   color        Tailwind background color class
 *   role         the user.role string this portal targets (used to detect
 *                  mismatches and offer "switch portal" prompts)
 */
// `public: true` ⇒ shown on the public Portal Selector landing page.
// All other portals still work end-to-end via direct URLs, PWA shortcuts
// (`/food`, `/hostel`, `/merchant`, etc.) and post-login routing — they are
// simply hidden from casual visitors. Admin is intentionally NOT registered
// here at all to keep its login surface non-discoverable.
export const PORTALS = [
  // Returning customer and provider accounts share sign-in by credential type.
  // Role-specific activation URLs below still preserve registration context.
  { slug: "customer",  loginPath: "/auth",                        label: "Customer",         icon: "Users",         color: "bg-[#10B981]", role: "customer",         public: true },
  { slug: "student",   loginPath: "/auth/pin",                     label: "Student",          icon: "GraduationCap", color: "bg-[#7C3AED]", role: "student",          public: true },
  { slug: "service",   loginPath: "/auth/pin",                     label: "Service Provider", icon: "Wrench",        color: "bg-[#06B6D4]", role: "service_provider", public: true },
  { slug: "food",      loginPath: "/auth/pin",                     label: "Food Vendor",      icon: "Utensils",      color: "bg-[#F59E0B]", role: "food_vendor",      public: true },
  { slug: "organizer", loginPath: "/auth/pin",                     label: "Event Organizer",  icon: "Calendar",      color: "bg-[#FB923C]", role: "organizer",        public: true },
  { slug: "iprint",    loginPath: "/auth/pin",                     label: "iPrint Shop",      icon: "Printer",       color: "bg-[#EC4899]", role: "iprint_shop",      public: true },
  { slug: "hostel",    loginPath: "/auth/pin",                     label: "Hostel Owner",     icon: "Building2",     color: "bg-[#0EA5E9]", role: "hostel_owner",     public: true },
  { slug: "staff",     loginPath: "/admin/icaard",                label: "Staff",            icon: "ShieldCheck",   color: "bg-[#64748B]", role: "staff",            public: true },
  { slug: "coordinator", loginPath: "/coordinator",               label: "Area Coordinator", icon: "MapPinned",     color: "bg-[#16A34A]", role: "area_coordinator", public: true },
  { slug: "driver",    loginPath: "/auth/pin",                     label: "Driver",           icon: "Bus",           color: "bg-[#3B82F6]", role: "driver",           public: false },
  { slug: "conductor", loginPath: "/auth/pin",                     label: "Conductor",        icon: "UserCheck",     color: "bg-[#8B5CF6]", role: "conductor",        public: true },
  { slug: "merchant",  loginPath: "/auth/pin",                     label: "Merchant",         icon: "Store",         color: "bg-[#14B8A6]", role: "merchant",         public: true },
];

/** Per-portal activation/registration target when a paid card is scanned.
 *  Feb 24 2026 — UNIFIED: every role now activates through the PIN portal
 *  (`/auth/pin?portal=<slug>&activate=<qr>`). The PIN page detects activation
 *  mode from the query and shows a single-page registration form.
 */
export const ACTIVATION_PATH = {
  customer:  "/auth/pin?portal=customer",
  student:   "/auth/pin?portal=student",
  driver:    "/auth/pin?portal=driver",
  iprint:    "/auth/pin?portal=iprint",
  food:      "/auth/pin?portal=food",
  hostel:    "/auth/pin?portal=hostel",
  organizer: "/auth/pin?portal=organizer",
  service:   "/auth/pin?portal=service",
  merchant:  "/register-merchant",
  conductor: "/conductor-join",
};

export const PUBLIC_PORTALS = PORTALS.filter(p => p.public);

export const findPortal = (slug) => PORTALS.find(p => p.slug === slug) || null;
export const findPortalByRole = (role) => PORTALS.find(p => p.role === role) || null;

const PortalCtx = createContext({ portal: null, set: () => {}, clear: () => {} });

export function PortalProvider({ children }) {
  const [portal, setPortal] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY); } catch { return null; }
  });

  // Cross-tab sync — if another tab changes the portal, mirror the change here.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) setPortal(e.newValue || null);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const set = useCallback((slug) => {
    if (!slug) return;
    try { localStorage.setItem(STORAGE_KEY, slug); } catch { /* */ }
    setPortal(slug);
  }, []);

  const clear = useCallback(() => {
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* */ }
    setPortal(null);
  }, []);

  return <PortalCtx.Provider value={{ portal, set, clear }}>{children}</PortalCtx.Provider>;
}

export const usePortal = () => useContext(PortalCtx);

/** Returns where an unauthenticated user should land. */
export function defaultUnauthedPath(slug) {
  const p = findPortal(slug);
  return p ? p.loginPath : "/login";
}
